import { __ } from '@wordpress/i18n';
import { Trash, DollarSign } from 'lucide-react';
import {
    DokanTooltip as Tooltip,
    DebouncedInput,
    DokanLink,
} from '@dokan/components';
import { truncate, formatPrice } from '@dokan/utilities';
import { RawHTML } from '@wordpress/element';
import { decodeEntities } from '@wordpress/html-entities';
import { twMerge } from 'tailwind-merge';

const ProductsTable = ( { data, onChange, isEditable } ) => {
    const items = data.products || [];

    const handleQuantityChange = ( index, value ) => {
        const newProducts = [ ...items ];
        newProducts[ index ] = {
            ...newProducts[ index ],
            quantity: parseInt( value ) || 1,
        };
        onChange( { products: newProducts } );
    };

    const handleOfferPriceChange = ( index, value ) => {
        const newProducts = [ ...items ];
        newProducts[ index ] = {
            ...newProducts[ index ],
            offer_price: parseFloat( value ) || 0,
        };
        onChange( { products: newProducts } );
    };

    const handleRemoveProduct = ( index ) => {
        onChange( {
            products: items.filter( ( _, i ) => i !== index ),
        } );
    };

    return (
        <div className="px-7 pb-4 pt-1">
            <table className="w-full">
                <thead>
                    <tr>
                        <th className="px-4 py-3 !text-xs font-semibold text-[#828282] uppercase tracking-wider text-left">
                            { __( 'Products & Cost', 'dokan' ) }
                        </th>
                        <th className="px-4 py-3 !text-xs font-semibold text-[#828282] uppercase tracking-wider text-left">
                            { __( 'QTY.', 'dokan' ) }
                        </th>
                        <th className="px-4 py-3 !text-xs font-semibold text-[#828282] uppercase tracking-wider text-left">
                            { __( 'Offer Price', 'dokan' ) }
                        </th>
                        <th className="px-4 py-3 !text-xs font-semibold text-[#828282] uppercase tracking-wider text-right">
                            { __( 'Subtotal', 'dokan' ) }
                        </th>
                        <th className="px-4 py-3"></th>
                    </tr>
                </thead>
                <tbody className="before:content-[''] before:block before:h-5">
                    { items.map( ( product, index ) => (
                        <tr
                            key={ index }
                            className="group h-16 last:h-12 align-top"
                        >
                            <td className="px-4 py-3">
                                <div className="flex items-center gap-4">
                                    { product.image ? (
                                        <img
                                            src={ product.image }
                                            alt={ product.name }
                                            className="w-12 h-12 object-cover rounded-md border border-[#E9E9E9]"
                                        />
                                    ) : (
                                        <div className="w-12 h-12 bg-gray-100 rounded-md border border-[#E9E9E9] flex items-center justify-center text-gray-400">
                                            { __( 'N/A', 'dokan' ) }
                                        </div>
                                    ) }
                                    <div className="space-y-1.5">
                                        <DokanLink
                                            target="_blank"
                                            href={ product.permalink }
                                            className="font-semibold text-sm text-[#7047EB]"
                                        >
                                            { product?.name?.length <= 22 ? (
                                                decodeEntities( product.name )
                                            ) : (
                                                <Tooltip
                                                    content={ decodeEntities(
                                                        product.name
                                                    ) }
                                                >
                                                    <span>
                                                        { decodeEntities(
                                                            truncate
                                                                ? truncate(
                                                                      product.name,
                                                                      22
                                                                  )
                                                                : product.name
                                                        ) }
                                                    </span>
                                                </Tooltip>
                                            ) }
                                        </DokanLink>
                                        <p className="text-sm text-[#575757]">
                                            <RawHTML>
                                                { formatPrice( product.price ) }
                                            </RawHTML>
                                        </p>
                                    </div>
                                </div>
                            </td>
                            <td className="px-4 py-3">
                                <div className="max-w-28">
                                    <DebouncedInput
                                        value={ product.quantity }
                                        onChange={ ( value ) =>
                                            handleQuantityChange( index, value )
                                        }
                                        input={ {
                                            type: 'number',
                                            id: `quantity-${ index }`,
                                            min: '1',
                                            step: '1',
                                        } }
                                        disabled={ ! isEditable }
                                        delay={ 1000 }
                                    />
                                </div>
                            </td>
                            <td className="px-4 py-3">
                                <div
                                    className={ twMerge(
                                        'max-w-28 w-fit',
                                        product.offer_price < 0 &&
                                            'border border-[#E64B5F] rounded-md overflow-hidden'
                                    ) }
                                >
                                    <DebouncedInput
                                        addOnLeft={
                                            dokanWithdrawDashboard?.currency
                                                ?.symbol || (
                                                <DollarSign size={ 20 } />
                                            )
                                        }
                                        value={ product.offer_price.toFixed(
                                            dokanAdminDashboard?.currency
                                                ?.precision || 2
                                        ) }
                                        onChange={ ( value ) =>
                                            handleOfferPriceChange(
                                                index,
                                                value
                                            )
                                        }
                                        input={ {
                                            type: 'number',
                                            id: `offer-price-${ index }`,
                                            min: '0',
                                            step: '1',
                                        } }
                                        disabled={ ! isEditable }
                                        delay={ 1000 }
                                    />
                                </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                                <div className="font-medium text-sm text-[#575757] h-10 flex items-center">
                                    <RawHTML>
                                        { formatPrice(
                                            product.offer_price *
                                                product.quantity
                                        ) }
                                    </RawHTML>
                                </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                                { isEditable && (
                                    <div className="h-10 flex items-center">
                                        <button
                                            onClick={ () =>
                                                handleRemoveProduct( index )
                                            }
                                            className="p-2 text-[#828282] hover:text-[#E64B5F] hover:bg-red-50 rounded-full transition-colors"
                                            title={ __(
                                                'Remove Product',
                                                'dokan'
                                            ) }
                                        >
                                            <Trash size={ 18 } />
                                        </button>
                                    </div>
                                ) }
                            </td>
                        </tr>
                    ) ) }
                </tbody>
            </table>
        </div>
    );
};

export default ProductsTable;
